


import React, { useState, useEffect } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import SeriesFareTabs from "../../Supplier/SeriesFares/SeriesFareTabs"; // Import the tabs component
import {
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiEyeLine,
  RiSearchLine,
  RiFilterLine,
  RiRefreshLine,
  RiTimeLine,
  RiCalendarLine,
  RiUserLine,
  RiPlaneLine,
  RiBuildingLine,
  RiArrowRightLine,
  RiCheckLine,
  RiAlertLine,
  RiFileListLine,
  RiLoader4Line,
  RiCheckDoubleLine,
  RiErrorWarningLine,
  RiUserSharedLine,
  RiHistoryLine,
  RiMailLine,
  RiPhoneLine
} from "react-icons/ri";

const DmcBlockRequests = () => {
  const { user } = useAuth();
  
  const [blockRequests, setBlockRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // Default to all
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchBlockRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [blockRequests, searchTerm, statusFilter]);

  const fetchBlockRequests = async () => {
    try {
      setLoading(true);
      
      // CORRECT ENDPOINT: Use /api/series-bookings/dmc/{dmcId}
      const response = await apiService.getAll(
       `${config.SERIESBOOKINGSREQUESTS}/${user?.cid}`
      );
      
      if (response && response.success) {
        setBlockRequests(response.bookings || []);
      } else {
        setBlockRequests([]);
      }
    } catch (error) {
      console.error("Error fetching block requests:", error);
      setMessage({ type: "error", text: "Failed to load block requests" });
      setBlockRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...blockRequests];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(request =>
        request.bookingId?.toLowerCase().includes(term) ||
        request.airlineCode?.toLowerCase().includes(term) ||
        request.agentName?.toLowerCase().includes(term) ||
        request.agentEmail?.toLowerCase().includes(term) ||
        request.from?.toLowerCase().includes(term) ||
        request.to?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== "ALL") {
      filtered = filtered.filter(request => request.bookingStatus === statusFilter);
    }

    setFilteredRequests(filtered);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus.toLowerCase()} this block request?`)) {
      return;
    }

    try {
      setProcessing(true);
      setMessage({ type: "", text: "" });

      const response = await apiService.put(
        config.SERIESBOOKINGSTATUS,
        {
          bookingId: bookingId,
          bookingStatus: newStatus
        }
      );

      if (response && response.success) {
        setMessage({ 
          type: "success", 
          text: `Booking status updated to ${newStatus}` 
        });
        
        // Update local state
        setBlockRequests(prev => 
          prev.map(req => 
            req.bookingId === bookingId 
              ? { ...req, bookingStatus: newStatus, updatedAt: new Date().toISOString() }
              : req
          )
        );

        // Close modal if open
        setShowDetailsModal(false);
        
        // Refresh after delay
        setTimeout(() => {
          fetchBlockRequests();
        }, 1000);
      } else {
        throw new Error(response?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setMessage({ 
        type: "error", 
        text: error.message || "Failed to update booking status" 
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR ${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "BLOCK REQUEST":
        return { 
          class: "badge bg-warning", 
          icon: <RiTimeLine />, 
          text: "BLOCK REQUEST",
          description: "Waiting for approval"
        };
      case "BLOCKED":
        return { 
          class: "badge bg-success", 
          icon: <RiCheckboxCircleLine />, 
          text: "BLOCKED",
          description: "Seats blocked"
        };
      case "REJECTED":
        return { 
          class: "badge bg-danger", 
          icon: <RiCloseCircleLine />, 
          text: "REJECTED",
          description: "Request rejected"
        };
      case "CONFIRMED":
        return { 
          class: "badge bg-primary", 
          icon: <RiCheckDoubleLine />, 
          text: "CONFIRMED",
          description: "Payment completed"
        };
      default:
        return { 
          class: "badge bg-secondary", 
          icon: <RiAlertLine />, 
          text: status,
          description: ""
        };
    }
  };

  const refreshData = () => {
    setMessage({ type: "info", text: "Refreshing data..." });
    fetchBlockRequests();
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} min ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else {
      return `${diffDays} days ago`;
    }
  };

  // Get pending requests count for tab title
  const pendingRequestsCount = blockRequests.filter(r => r.bookingStatus === "BLOCK REQUEST").length;

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                {/* <SeriesFareTabs pageName="SERIESFAREREQUESTS" /> */}
              </div>
              <div className="card-body">
                {/* Page Header */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="card-title mb-1">Block Requests Management</h5>
                        <p className="text-muted mb-0">Review and manage seat block requests from agents</p>
                      </div>
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-outline-primary"
                          onClick={refreshData}
                          disabled={loading || processing}
                        >
                          <RiRefreshLine className={`me-1 ${loading ? 'spinning' : ''}`} />
                          Refresh
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message Alert */}
                {message.text && (
                  <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                    {message.type === "success" ? <RiCheckLine className="me-2" /> : 
                     message.type === "error" ? <RiErrorWarningLine className="me-2" /> : 
                     <RiLoader4Line className="me-2 spinning" />}
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage({ type: "", text: "" })}></button>
                  </div>
                )}

                {/* Filters */}
                <div className="row mb-4">
                  <div className="col-md-8">
                    <div className="input-group">
                      <span className="input-group-text">
                        <RiSearchLine />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Booking ID, Agent, Airline, Route..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <button className="btn btn-outline-secondary" type="button">
                        <RiFilterLine />
                      </button>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="ALL">All Status</option>
                      <option value="BLOCK REQUEST">Block Request (Pending)</option>
                      <option value="BLOCKED">Blocked (Approved)</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="CONFIRMED">Confirmed (Paid)</option>
                    </select>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card bg-warning bg-opacity-10 border-warning">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-warning">Pending Requests</h6>
                            <h3 className="mb-0">
                              {blockRequests.filter(r => r.bookingStatus === "BLOCK REQUEST").length}
                            </h3>
                            <small className="text-muted">Awaiting approval</small>
                          </div>
                          <RiTimeLine className="text-warning" size={40} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-success bg-opacity-10 border-success">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-success">Blocked</h6>
                            <h3 className="mb-0">
                              {blockRequests.filter(r => r.bookingStatus === "BLOCKED").length}
                            </h3>
                            <small className="text-muted">Seats reserved</small>
                          </div>
                          <RiCheckboxCircleLine className="text-success" size={40} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-danger bg-opacity-10 border-danger">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-danger">Rejected</h6>
                            <h3 className="mb-0">
                              {blockRequests.filter(r => r.bookingStatus === "REJECTED").length}
                            </h3>
                            <small className="text-muted">Requests declined</small>
                          </div>
                          <RiCloseCircleLine className="text-danger" size={40} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-primary bg-opacity-10 border-primary">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-primary">Confirmed</h6>
                            <h3 className="mb-0">
                              {blockRequests.filter(r => r.bookingStatus === "CONFIRMED").length}
                            </h3>
                            <small className="text-muted">Payment completed</small>
                          </div>
                          <RiCheckDoubleLine className="text-primary" size={40} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Block Requests Table */}
                <div className="card">
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        Block Requests
                        {filteredRequests.length > 0 && (
                          <span className="text-muted ms-2">
                            ({filteredRequests.length} requests found)
                          </span>
                        )}
                      </h5>
                      <div className="text-muted small">
                        {pendingRequestsCount > 0 && (
                          <span className="text-warning fw-bold">
                            {pendingRequestsCount} pending approval
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading block requests...</p>
                      </div>
                    ) : filteredRequests.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="display-1 text-muted mb-3">
                          <RiFileListLine />
                        </div>
                        <h5>No Block Requests Found</h5>
                        <p className="text-muted">
                          {blockRequests.length === 0 
                            ? "No block requests available at the moment."
                            : "No requests match your search criteria."}
                        </p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-centered table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Booking ID</th>
                              <th>Agent Details</th>
                              <th>Flight Details</th>
                              <th>Pax</th>
                              <th>Amount</th>
                              <th>Requested</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRequests.map((request) => {
                              const statusBadge = getStatusBadge(request.bookingStatus);
                              const isPending = request.bookingStatus === "BLOCK REQUEST";
                              
                              return (
                                <tr key={request.bookingId}>
                                  <td>
                                    <div className="fw-semibold text-primary">
                                      {request.bookingId?.substring(0, 8)}...
                                    </div>
                                    <div className="text-muted small">
                                      {request.seriesFareId?.substring(0, 6)}...
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fw-semibold">{request.agentName}</div>
                                    <div className="text-muted small">
                                      <RiMailLine className="me-1" />
                                      {request.agentEmail || "No email"}
                                    </div>
                                    {request.agentPhone && (
                                      <div className="text-muted small">
                                        <RiPhoneLine className="me-1" />
                                        {request.agentPhone}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                                           style={{width: '28px', height: '28px'}}>
                                        <RiPlaneLine size={14} />
                                      </div>
                                      <div>
                                        <div className="fw-semibold">
                                          {request.from} → {request.to}
                                        </div>
                                        <div className="text-muted small">
                                          {request.airlineCode} • {formatDate(request.travelDate)}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="text-center">
                                      <h5 className="mb-0">{request.paxCount}</h5>
                                      <small className="text-muted">Passengers</small>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="text-end">
                                      <div className="fw-bold text-success">
                                        {formatCurrency(request.totalAmount)}
                                      </div>
                                      <div className="text-muted small">
                                        {formatCurrency(request.pricePerSeat)} per person
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      <div className="fw-semibold">{formatDate(request.createdAt)}</div>
                                      <small className="text-muted">
                                        {getTimeSince(request.createdAt)}
                                      </small>
                                    </div>
                                  </td>
                                  <td>
                                    <span className={statusBadge.class}>
                                      {statusBadge.icon} {statusBadge.text}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleViewDetails(request)}
                                        title="View Details"
                                      >
                                        <RiEyeLine />
                                      </button>
                                      
                                      {isPending && (
                                        <>
                                          <button
                                            className="btn btn-sm btn-success"
                                            onClick={() => handleUpdateStatus(request.bookingId, "BLOCKED")}
                                            disabled={processing}
                                            title="Approve Block"
                                          >
                                            <RiCheckLine />
                                          </button>
                                          <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleUpdateStatus(request.bookingId, "REJECTED")}
                                            disabled={processing}
                                            title="Reject Request"
                                          >
                                            <RiCloseCircleLine />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Block Request Details</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDetailsModal(false)}
                  disabled={processing}
                ></button>
              </div>
              
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Booking Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-2">
                          <small className="text-muted">Booking ID</small>
                          <div className="fw-bold">{selectedRequest.bookingId}</div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Fare ID</small>
                          <div className="fw-bold">{selectedRequest.seriesFareId}</div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Current Status</small>
                          <div>
                            <span className={getStatusBadge(selectedRequest.bookingStatus).class}>
                              {selectedRequest.bookingStatus}
                            </span>
                          </div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Booking Dates</small>
                          <div>
                            <div>Created: {formatDate(selectedRequest.createdAt)}</div>
                            <div>Updated: {formatDate(selectedRequest.updatedAt)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Agent Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-2">
                          <small className="text-muted">Agent Name</small>
                          <div className="fw-bold">{selectedRequest.agentName}</div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Agent ID</small>
                          <div>{selectedRequest.agentId}</div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Contact Email</small>
                          <div>
                            <RiMailLine className="me-1" />
                            {selectedRequest.agentEmail || "Not available"}
                          </div>
                        </div>
                        {selectedRequest.agentPhone && (
                          <div className="mb-2">
                            <small className="text-muted">Contact Phone</small>
                            <div>
                              <RiPhoneLine className="me-1" />
                              {selectedRequest.agentPhone}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Flight Details</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-12">
                            <div className="mb-2">
                              <small className="text-muted">Route</small>
                              <h5>
                                <RiPlaneLine className="me-2" />
                                {selectedRequest.from} → {selectedRequest.to}
                              </h5>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="mb-2">
                              <small className="text-muted">Airline</small>
                              <div className="fw-bold">{selectedRequest.airlineCode}</div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="mb-2">
                              <small className="text-muted">Travel Date</small>
                              <div className="fw-bold">
                                <RiCalendarLine className="me-1" />
                                {formatDate(selectedRequest.travelDate)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Booking Summary</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-6">
                            <div className="mb-2">
                              <small className="text-muted">Passengers</small>
                              <h4>
                                <RiUserLine className="me-2" />
                                {selectedRequest.paxCount}
                              </h4>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="mb-2">
                              <small className="text-muted">Price per Seat</small>
                              <h4 className="text-success">
                                {formatCurrency(selectedRequest.pricePerSeat)}
                              </h4>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                              <div>
                                <strong>Total Amount</strong>
                                <div className="small text-muted">
                                  {selectedRequest.paxCount} passenger{selectedRequest.paxCount > 1 ? 's' : ''}
                                </div>
                              </div>
                              <div>
                                <h3 className="text-success mb-0">
                                  {formatCurrency(selectedRequest.totalAmount)}
                                </h3>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                {selectedRequest.bookingStatus === "BLOCK REQUEST" && (
                  <>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => {
                        handleUpdateStatus(selectedRequest.bookingId, "BLOCKED");
                      }}
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <RiCheckLine className="me-2" />
                          Approve Block
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        handleUpdateStatus(selectedRequest.bookingId, "REJECTED");
                      }}
                      disabled={processing}
                    >
                      <RiCloseCircleLine className="me-2" />
                      Reject Request
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                  disabled={processing}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DmcBlockRequests;